import React, { useState, useMemo, useEffect } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery } from '@tanstack/react-query';
import { useLocation, Link } from 'react-router-dom';
import { Loader2, SlidersHorizontal, X } from 'lucide-react';
import CSProductCard from '@/components/products/CSProductCard';
import CSLogoBanner from '@/components/home/CSLogoBanner';
import usePageTitle from '@/hooks/usePageTitle';
import { CATEGORIES, CATEGORY_VALUES, categoryFromUrl, categoryUrlSlug, subCategoryFromUrl, subCategoryUrlSlug } from '@/utils/categories';

const FILTER_CATEGORIES = [{ value: 'all', label: 'Toutes les catégories', subs: [] }, ...CATEGORIES];


const SORT_OPTIONS = [
  { value: 'default', label: 'Pertinence' },
  { value: 'price-asc', label: 'Prix croissant' },
  { value: 'price-desc', label: 'Prix décroissant' },
  { value: 'newest', label: 'Nouveautés' },
  { value: 'rating', label: 'Meilleures notes' },
];

// Title + meta description uniques par catégorie (SEO)
const CATEGORY_META = {
  'all': {
    title: 'Boutique Circuit de Slot',
    description: 'Catalogue complet Circuit-Slot : coffrets, voitures, rails, accessoires et pièces détachées Scalextric & Carrera, analogique et digital. Livraison 48/72h.',
  },
  'coffrets-circuits': {
    title: 'Coffrets Circuits de Slot',
    description: 'Circuit-Slot : coffrets circuits de slot complets Scalextric & Carrera, analogiques et digitaux, prêts à rouler en famille. Large choix en stock, livraison 48/72h.',
  },
  'voitures': {
    title: 'Voitures de Slot',
    description: 'Circuit-Slot : voitures de slot 1/32, 1/24 et 1/43 Scalextric, Carrera, NSR et SCX. Modèles analogiques et digitaux neufs au meilleur prix. Livraison 48/72h.',
  },
  'equipements-accessoires': {
    title: 'Accessoires pour Circuit de Slot',
    description: 'Circuit-Slot : accessoires pour circuit de slot — contrôleurs, alimentations, chronomètres et rangements. Compatibles Scalextric & Carrera. Livraison 48/72h.',
  },
  'equipements-rails': {
    title: 'Rails & Voies pour Circuit de Slot',
    description: 'Circuit-Slot : rails, voies droites et courbes, changements de voie et raccords pour circuit de slot Scalextric & Carrera. Toutes les échelles. Livraison 48/72h.',
  },
  'pieces-detachees': {
    title: 'Pièces Détachées Slot',
    description: 'Circuit-Slot : pièces détachées pour circuits de slot — guides, balais, axes, couronnes, pneus et vis. Réparez et optimisez vos voitures Scalextric & Carrera.',
  },
  'decor': {
    title: 'Décor pour Circuit de Slot',
    description: 'Circuit-Slot : décor pour circuit de slot — tribunes, bordures, gabarits, bandes adhésives et accessoires de décoration Scalextric & Carrera. Livraison 48/72h.',
  },
};

export default function CSShop() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const searchQuery = params.get('q') || '';
  const promoOnly = params.get('promo') === 'true';
  const [category, setCategory] = useState(categoryFromUrl(params.get('cat')));
  const [subCategory, setSubCategory] = useState(subCategoryFromUrl(params.get('sub')));
  const [brand, setBrand] = useState('');
  const [sort, setSort] = useState('default');
  const [priceMax, setPriceMax] = useState(2000);
  const [showFilters, setShowFilters] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [scale, setScale] = useState('');
  const [systeme, setSysteme] = useState('');

  React.useEffect(() => {
    const p = new URLSearchParams(location.search);
    const rawCat = p.get('cat');
    const rawSub = p.get('sub');
    setCategory(categoryFromUrl(rawCat));
    setSubCategory(subCategoryFromUrl(rawSub));

    // Anciennes adresses (equipements-accessoires, sub=rails…) → slug français, sans casser le lien
    const nextCat = rawCat ? categoryUrlSlug(categoryFromUrl(rawCat)) : null;
    const nextSub = rawSub ? subCategoryUrlSlug(subCategoryFromUrl(rawSub)) : null;
    if ((nextCat && nextCat !== rawCat) || (nextSub && nextSub !== rawSub)) {
      const next = new URLSearchParams(location.search);
      if (nextCat) next.set('cat', nextCat);
      if (nextSub) next.set('sub', nextSub);
      window.history.replaceState({}, '', `${location.pathname}?${next.toString()}`);
    }
  }, [location.search]);

  // Catégories à interroger selon le filtre actif
  const categoriesToFetch = React.useMemo(() => {
    if (category === 'all') return CATEGORY_VALUES;
    return [category];
  }, [category]);

  const { data: products = [], isLoading } = useQuery({
    queryKey: ['cs-products', category],
    queryFn: async () => {
      if (category === 'all') {
        return base44.entities.Product.list('-created_date', 5000);
      }
      const results = await Promise.all(
        categoriesToFetch.map(cat => base44.entities.Product.filter({ category: cat }, '-created_date', 5000))
      );
      const allProducts = results.flat();
      const seen = new Set();
      return allProducts.filter(p => { if (seen.has(p.id)) return false; seen.add(p.id); return true; });
    },
    staleTime: 5 * 60 * 1000,
  });

  const brands = useMemo(() => {
    const source = promoOnly ? products.filter(p => p.compare_price && p.compare_price > p.price) : products;
    const set = new Set(source.map(p => p.brand).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [products, promoOnly]);

  const scales = useMemo(() => {
    const set = new Set(products.map(p => p.scale).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [products]);

  const systemes = useMemo(() => {
    const set = new Set(products.map(p => p.systeme).filter(Boolean));
    return [...set].sort((a, b) => a.localeCompare(b, 'fr', { sensitivity: 'base' }));
  }, [products]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (category !== 'all') {
      list = list.filter(p => p.category === category);
    }
    if (subCategory) list = list.filter(p => p.sub_category === subCategory || (p.sub_categories_secondary || []).includes(subCategory));
    if (brand) list = list.filter(p => p.brand?.toLowerCase() === brand.toLowerCase());
    if (scale) list = list.filter(p => p.scale === scale);
    if (systeme) list = list.filter(p => p.systeme === systeme);
    if (searchQuery) list = list.filter(p =>
      p.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.brand?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.reference?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (priceMax < 2000) list = list.filter(p => p.price <= priceMax);
    if (inStockOnly) list = list.filter(p => p.stock_quantity === undefined || p.stock_quantity === null || p.stock_quantity > 0);
    if (promoOnly) list = list.filter(p => p.compare_price && p.compare_price > p.price);
    if (sort === 'price-asc') list.sort((a, b) => a.price - b.price);
    else if (sort === 'price-desc') list.sort((a, b) => b.price - a.price);
    else if (sort === 'rating') list.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return list;
  }, [products, category, subCategory, brand, searchQuery, priceMax, sort, inStockOnly, scale, systeme]);

  const catLabel = CATEGORIES.find(c => c.value === category)?.label || 'Catalogue';
  const catMeta = CATEGORY_META[category] || CATEGORY_META['all'];
  usePageTitle(
    promoOnly ? 'Promotions Slot' : searchQuery ? `Recherche : "${searchQuery}"` : catMeta.title,
    promoOnly
      ? 'Profitez des promotions Circuit-Slot sur les coffrets, voitures et accessoires de slot Scalextric & Carrera. Réductions et ventes flash, livraison 48/72h.'
      : searchQuery
        ? `Résultats de recherche pour "${searchQuery}" sur Circuit-Slot : coffrets, voitures, rails et accessoires de slot à acheter en ligne.`
        : catMeta.description
  );

  // Canonical + noindex : éviter les pages dupliquées (filtres, recherche, promo, cat invalide)
  const VALID_CATS = ['all', ...CATEGORY_VALUES];
  useEffect(() => {
    const isValidCat = VALID_CATS.includes(category);
    const canonicalCat = isValidCat ? category : 'all';
    const canonicalUrl = canonicalCat === 'all'
      ? 'https://circuit-slot.fr/shop'
      : `https://circuit-slot.fr/shop?cat=${categoryUrlSlug(canonicalCat)}`;

    let link = document.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonicalUrl);

    const hasFilters = !!(brand || scale || systeme || searchQuery || promoOnly || sort !== 'default' || inStockOnly || priceMax < 2000);
    const shouldNoindex = hasFilters || !isValidCat;

    let robots = document.querySelector('meta[name="robots"]');
    const original = robots?.getAttribute('content') || null;
    if (shouldNoindex) {
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, follow');
    } else if (robots) {
      robots.setAttribute('content', 'index, follow');
    }

    return () => {
      if (link) link.setAttribute('href', 'https://circuit-slot.fr/');
      if (shouldNoindex && robots) {
        if (original) robots.setAttribute('content', original);
        else robots.remove();
      }
    };
  }, [category, brand, scale, systeme, searchQuery, promoOnly, sort, inStockOnly, priceMax]);

  // Breadcrumb Schema.org
  useEffect(() => {
    const items = [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://circuit-slot.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Boutique', item: 'https://circuit-slot.fr/shop' },
    ];
    if (category !== 'all') {
      items.push({ '@type': 'ListItem', position: 3, name: catLabel, item: `https://circuit-slot.fr/shop?cat=${categoryUrlSlug(category)}` });
    }
    const jsonLd = { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: items };
    let script = document.querySelector('script[data-shop-breadcrumb]');
    if (!script) {
      script = document.createElement('script');
      script.setAttribute('type', 'application/ld+json');
      script.setAttribute('data-shop-breadcrumb', '1');
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(jsonLd);
    return () => { if (script) script.remove(); };
  }, [category, catLabel]);

  const Filters = () => (
    <aside className="space-y-6">
      {/* Categories */}
      <div>
        <h3 className="font-display font-semibold text-gray-800 mb-3 text-sm uppercase">Catégories</h3>
        <div className="space-y-1">
          {FILTER_CATEGORIES.map(c => (
            <React.Fragment key={c.value}>
              <button
                onClick={() => { setCategory(c.value); setSubCategory(''); }}
                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${
                  category === c.value ? 'bg-cs-blue text-white font-medium' : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                {c.label}
              </button>
              {c.subs.map(s => (
                <button
                  key={s.value}
                  onClick={() => { setCategory(c.value); setSubCategory(category === c.value && subCategory === s.value ? '' : s.value); }}
                  className={`w-full text-left pl-6 pr-3 py-1.5 rounded text-sm transition-colors ${
                    subCategory === s.value ? 'bg-cs-blue text-white font-medium' : 'text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  ↳ {s.label}
                </button>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Brands */}
      <div>
        <h3 className="font-display font-semibold text-gray-800 mb-3 text-sm uppercase">Marques</h3>
        <div className="space-y-1">
          <button
            onClick={() => setBrand('')}
            className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${!brand ? 'text-cs-blue font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
          >
            Toutes les marques
          </button>
          {brands.map(b => (
            <button
              key={b}
              onClick={() => setBrand(b === brand ? '' : b)}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${brand === b ? 'bg-cs-blue text-white font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              {b}
            </button>
          ))}
        </div>
      </div>

      {/* Échelle */}
      {scales.length > 0 && (
        <div>
          <h3 className="font-display font-semibold text-gray-800 mb-3 text-sm uppercase">Échelle</h3>
          <div className="space-y-1">
            <button
              onClick={() => setScale('')}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${!scale ? 'text-cs-blue font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Toutes les échelles
            </button>
            {scales.map(s => (
              <button
                key={s}
                onClick={() => setScale(s === scale ? '' : s)}
                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${scale === s ? 'bg-cs-blue text-white font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Système */}
      {systemes.length > 0 && (
        <div>
          <h3 className="font-display font-semibold text-gray-800 mb-3 text-sm uppercase">Système</h3>
          <div className="space-y-1">
            <button
              onClick={() => setSysteme('')}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-colors ${!systeme ? 'text-cs-blue font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              Tous les systèmes
            </button>
            {systemes.map(s => (
              <button
                key={s}
                onClick={() => setSysteme(s === systeme ? '' : s)}
                className={`w-full text-left px-3 py-2 rounded text-sm transition-colors capitalize ${systeme === s ? 'bg-cs-blue text-white font-medium' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* In stock */}
      <div>
        <label className="flex items-center gap-2 cursor-pointer px-3 py-2 rounded hover:bg-gray-100 transition-colors">
          <input
            type="checkbox"
            checked={inStockOnly}
            onChange={e => setInStockOnly(e.target.checked)}
            className="w-4 h-4 accent-blue-600"
          />
          <span className="text-sm text-gray-700 font-medium">En stock uniquement</span>
        </label>
      </div>

    </aside>
  );

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav aria-label="Fil d'Ariane" className="text-xs text-gray-400 mb-6 flex items-center gap-1 flex-wrap">
        <Link to="/" className="hover:text-cs-blue transition-colors">Accueil</Link>
        <span>/</span>
        <Link to="/shop" className="hover:text-cs-blue transition-colors">Boutique</Link>
        {category !== 'all' && (
          <>
            <span>/</span>
            <span className="text-gray-700">{catLabel}</span>
          </>
        )}
        {searchQuery && (
          <>
            <span>/</span>
            <span className="text-gray-700">Recherche : "{searchQuery}"</span>
          </>
        )}
      </nav>

      <div className="flex gap-8">
        {/* Desktop Filters */}
        <div className="hidden md:block w-52 shrink-0">
          <Filters />
        </div>

        {/* Main content */}
        <div className="flex-1">
          {/* Header */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div>
              <h1 className="font-display font-bold text-2xl text-gray-900">{catLabel}</h1>
              <p className="text-sm text-gray-500">{filtered.length} produit{filtered.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="md:hidden flex items-center gap-2 border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <SlidersHorizontal className="w-4 h-4" /> Filtres
              </button>
              <select
                value={sort}
                onChange={e => setSort(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-cs-blue"
              >
                {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="md:hidden mb-6 p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="font-bold">Filtres</h3>
                <button onClick={() => setShowFilters(false)}><X className="w-4 h-4" /></button>
              </div>
              <Filters />
            </div>
          )}

          {/* Active filters */}
          {(category !== 'all' || subCategory || brand || scale || systeme) && (
            <div className="flex gap-2 flex-wrap mb-4">
              {category !== 'all' && (
                <span className="flex items-center gap-1 bg-cs-blue/10 text-cs-blue text-xs px-2 py-1 rounded-full">
                  {CATEGORIES.find(c => c.value === category)?.label}
                  <button onClick={() => setCategory('all')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {subCategory && (
                <span className="flex items-center gap-1 bg-cs-blue/10 text-cs-blue text-xs px-2 py-1 rounded-full">
                  ↳ {CATEGORIES.flatMap(c => c.subs).find(s => s.value === subCategory)?.label}
                  <button onClick={() => setSubCategory('')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {brand && (
                <span className="flex items-center gap-1 bg-cs-blue/10 text-cs-blue text-xs px-2 py-1 rounded-full">
                  {brand}
                  <button onClick={() => setBrand('')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {scale && (
                <span className="flex items-center gap-1 bg-cs-blue/10 text-cs-blue text-xs px-2 py-1 rounded-full">
                  Échelle {scale}
                  <button onClick={() => setScale('')}><X className="w-3 h-3" /></button>
                </span>
              )}
              {systeme && (
                <span className="flex items-center gap-1 bg-cs-blue/10 text-cs-blue text-xs px-2 py-1 rounded-full capitalize">
                  {systeme}
                  <button onClick={() => setSysteme('')}><X className="w-3 h-3" /></button>
                </span>
              )}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-cs-blue" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-gray-500 text-lg mb-2">Aucun produit trouvé</p>
              <button onClick={() => { setCategory('all'); setSubCategory(''); setBrand(''); setScale(''); setSysteme(''); }} className="text-cs-blue text-sm hover:underline">
                Effacer les filtres
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map(p => <CSProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>

      {promoOnly && <CSLogoBanner />}
    </div>
  );
}